import React, { useState } from 'react';
import { HeadphonesAffordance } from '../../Common/DesignComponents.jsx';
import RadioDramaBuilder from './RadioDramaBuilder.jsx';
import '../../../styles/theme.css';

export function FoleyPlayer({
  room = {},
  socket = null,
  playerId = '',
  audioEngine = null,
  onSendAction = null
}) {
  const modeState = room.modeState || {};
  const currentPhase = room.currentPhase || modeState.currentPhase || 'authoring';
  const assignedPrompt = modeState.assignedPrompts?.[playerId] || modeState.prompts?.[0] || { text: 'A heist goes wrong in a vault' };

  const [authorInput, setAuthorInput] = useState('');
  const [authorSubmitted, setAuthorSubmitted] = useState(false);
  const [sequenceSubmitted, setSequenceSubmitted] = useState(false);
  const [voteSubmitted, setVoteSubmitted] = useState(false);
  const [votedChoice, setVotedChoice] = useState(null);

  const dispatchAction = (payload) => {
    if (onSendAction) {
      onSendAction(payload);
    } else if (socket) {
      socket.emit('submit_action', {
        roomCode: room?.roomCode,
        ...payload
      });
    }
  };

  const handleAuthorSubmit = (e) => {
    e.preventDefault();
    if (!authorInput.trim()) return;

    dispatchAction({
      actionType: 'author_prompt',
      prompt: authorInput.trim(),
      text: authorInput.trim()
    });
    setAuthorSubmitted(true);
  };

  const handleSequenceSubmit = (sequenceData) => {
    dispatchAction({
      actionType: 'submit_foley_sequence',
      ...sequenceData
    });
    setSequenceSubmitted(true);
  };

  const handleSkipPrompt = () => {
    dispatchAction({
      actionType: 'skip_prompt'
    });
  };

  const handleVoteSubmit = (choice) => {
    dispatchAction({
      actionType: 'submit_vote',
      vote: choice
    });
    setVoteSubmitted(true);
    setVotedChoice(choice);
  };

  // 1. Authoring Phase: Write Scenario Prompt
  if (currentPhase === 'authoring') {
    return (
      <div
        className="foley-player-authoring"
        data-testid="foley-player-authoring"
        style={{
          width: '100%',
          color: 'var(--sd-ink, #EDEFF2)',
          fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
        }}
      >
        <HeadphonesAffordance message="headphones recommended" />

        <div
          style={{
            padding: '24px 20px',
            background: 'var(--sd-panel, #0D1015)',
            border: '1px solid var(--sd-line, #1A1F25)',
            borderRadius: '16px',
          }}
        >
          <div style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--sd-ink, #EDEFF2)', marginBottom: '8px' }}>
            🎭 Write a Dramatic Scenario
          </div>
          <div style={{ fontSize: '0.95rem', color: 'var(--sd-muted, #79838D)', marginBottom: '20px', lineHeight: 1.5 }}>
            Write a short scene setting for another player to bring to life with sound effects (e.g. "A heist goes wrong").
          </div>

          {authorSubmitted ? (
            <div data-testid="authoring-confirmed" style={{ textAlign: 'center', padding: '20px 0', color: 'var(--sd-sage, #8FC7A0)', fontWeight: 700 }}>
              ✅ Scenario submitted! Waiting for other players...
            </div>
          ) : (
            <form onSubmit={handleAuthorSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <input
                placeholder="e.g. A heist goes wrong in an underground vault"
                value={authorInput}
                onChange={(e) => setAuthorInput(e.target.value)}
                maxLength={120}
                required
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  fontSize: '15px',
                  borderRadius: '12px',
                  border: '1px solid var(--sd-line-2, #1C2229)',
                  background: 'var(--sd-panel-2, #0E1116)',
                  color: 'var(--sd-ink, #EDEFF2)',
                  boxSizing: 'border-box',
                  outline: 'none',
                  fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
                }}
              />
              <button
                type="submit"
                disabled={!authorInput.trim()}
                style={{
                  width: '100%',
                  height: '54px',
                  minHeight: '44px',
                  borderRadius: '14px',
                  background: !authorInput.trim() ? 'var(--sd-panel-2, #0E1116)' : 'var(--sd-teal, #3ACFCF)',
                  border: !authorInput.trim() ? '1px solid var(--sd-line, #1A1F25)' : '1px solid var(--sd-teal, #3ACFCF)',
                  color: !authorInput.trim() ? 'var(--sd-dim, #6E7982)' : 'var(--sd-ground, #08090B)',
                  fontWeight: 700,
                  fontSize: '15px',
                  cursor: !authorInput.trim() ? 'not-allowed' : 'pointer',
                  fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
                  transition: 'all 0.15s ease',
                }}
              >
                Submit Scenario
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  // 2. Answering Phase: Build 3-Clip Foley Scene
  if (currentPhase === 'answering') {
    if (sequenceSubmitted) {
      return (
        <div
          className="foley-player-answering-done"
          data-testid="foley-player-answering-done"
          style={{
            width: '100%',
            color: 'var(--sd-ink, #EDEFF2)',
            fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
          }}
        >
          <HeadphonesAffordance message="headphones recommended" />
          <div
            style={{
              padding: '36px 24px',
              textAlign: 'center',
              background: 'var(--sd-panel, #0D1015)',
              border: '1px solid var(--sd-line, #1A1F25)',
              borderRadius: '16px',
            }}
          >
            <div style={{ fontSize: '3rem', marginBottom: '12px' }}>🎬</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--sd-sage, #8FC7A0)', marginBottom: '8px' }}>
              Scene Submitted!
            </div>
            <div style={{ color: 'var(--sd-muted, #79838D)', lineHeight: 1.5 }}>
              Your 3-clip Foley scene is locked in. Watch the host screen for the grand duel reveal!
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="foley-player-answering" data-testid="foley-player-answering" style={{ width: '100%' }}>
        <HeadphonesAffordance message="headphones recommended for auditioning" />
        <RadioDramaBuilder
          assignedPrompt={assignedPrompt}
          onSubmit={handleSequenceSubmit}
          audioEngine={audioEngine}
          onSkipPrompt={handleSkipPrompt}
        />
      </div>
    );
  }

  // 3. Duel Phase: Voting on Foley Scenes
  if (currentPhase === 'duel') {
    const duels = modeState.duels || [];
    const currentDuelIndex = modeState.currentDuelIndex || 0;
    const currentDuel = duels[currentDuelIndex];

    if (!currentDuel) {
      return (
        <div
          style={{
            padding: '28px',
            textAlign: 'center',
            background: 'var(--sd-panel, #0D1015)',
            border: '1px solid var(--sd-line, #1A1F25)',
            borderRadius: '16px',
            color: 'var(--sd-dim, #6E7982)',
          }}
        >
          Preparing upcoming duels...
        </div>
      );
    }

    const isDuelist = currentDuel.duelistAId === playerId || currentDuel.duelistBId === playerId;
    const scenarioText = currentDuel.prompt ? currentDuel.prompt.text : 'Foley Scene Duel';

    if (isDuelist) {
      return (
        <div
          className="foley-player-duelist"
          data-testid="foley-player-duelist"
          style={{
            width: '100%',
            color: 'var(--sd-ink, #EDEFF2)',
            fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
          }}
        >
          <div
            style={{
              padding: '32px 24px',
              textAlign: 'center',
              background: 'var(--sd-panel, #0D1015)',
              border: '1px solid var(--sd-teal, #3ACFCF)',
              borderRadius: '16px',
            }}
          >
            <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>⚔️</div>
            <div style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--sd-ink, #EDEFF2)', marginBottom: '8px' }}>
              You are in this Duel!
            </div>
            <div style={{ fontSize: '1.1rem', color: 'var(--sd-teal, #3ACFCF)', fontWeight: 700, marginBottom: '16px' }}>
              "{scenarioText}"
            </div>
            <div style={{ color: 'var(--sd-muted, #79838D)', fontSize: '0.95rem' }}>
              Listen to the host screen playback. Other players are voting on your Foley scene!
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        className="foley-player-voter"
        data-testid="foley-player-voter"
        style={{
          width: '100%',
          color: 'var(--sd-ink, #EDEFF2)',
          fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
        }}
      >
        <div
          style={{
            padding: '20px',
            marginBottom: '16px',
            textAlign: 'center',
            background: 'var(--sd-panel, #0D1015)',
            border: '1px solid var(--sd-line, #1A1F25)',
            borderRadius: '16px',
          }}
        >
          <div style={{
            fontSize: '11px',
            color: 'var(--sd-dim, #6E7982)',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
            fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
          }}>
            Scenario Duel Matchup
          </div>
          <div style={{ fontSize: '1.2rem', fontWeight: 800, color: 'var(--sd-ink, #EDEFF2)', marginTop: '4px' }}>
            "{scenarioText}"
          </div>
        </div>

        {voteSubmitted ? (
          <div
            style={{
              padding: '32px 24px',
              textAlign: 'center',
              background: 'var(--sd-panel, #0D1015)',
              border: '1px solid var(--sd-line, #1A1F25)',
              borderRadius: '16px',
            }}
          >
            <div style={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--sd-sage, #8FC7A0)', marginBottom: '6px' }}>
              ✅ Vote Cast for Scene {votedChoice}!
            </div>
            <div style={{ color: 'var(--sd-muted, #79838D)' }}>Waiting for all votes to be recorded...</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--sd-dim, #6E7982)', textAlign: 'center' }}>
              Vote for the best Foley Scene:
            </div>
            <button
              onClick={() => handleVoteSubmit('A')}
              data-testid="vote-a-btn"
              style={{
                width: '100%',
                height: '54px',
                minHeight: '44px',
                borderRadius: '14px',
                background: 'var(--sd-teal, #3ACFCF)',
                border: '1px solid var(--sd-teal, #3ACFCF)',
                color: 'var(--sd-ground, #08090B)',
                fontWeight: 700,
                fontSize: '15px',
                cursor: 'pointer',
                fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
                transition: 'all 0.15s ease',
              }}
            >
              🅰️ Vote Scene A ({currentDuel.duelistA?.name || 'Player A'})
            </button>
            <button
              onClick={() => handleVoteSubmit('B')}
              data-testid="vote-b-btn"
              style={{
                width: '100%',
                height: '54px',
                minHeight: '44px',
                borderRadius: '14px',
                background: 'var(--sd-amber, #E0A458)',
                border: '1px solid var(--sd-amber, #E0A458)',
                color: 'var(--sd-ground, #08090B)',
                fontWeight: 700,
                fontSize: '15px',
                cursor: 'pointer',
                fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
                transition: 'all 0.15s ease',
              }}
            >
              🅱️ Vote Scene B ({currentDuel.duelistB?.name || 'Player B'})
            </button>
          </div>
        )}
      </div>
    );
  }

  // 4. Results / Default
  return (
    <div
      style={{
        padding: '32px 24px',
        textAlign: 'center',
        background: 'var(--sd-panel, #0D1015)',
        border: '1px solid var(--sd-line, #1A1F25)',
        borderRadius: '16px',
        color: 'var(--sd-ink, #EDEFF2)',
        fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
      }}
    >
      <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--sd-amber, #E0A458)', marginBottom: '8px' }}>
        🏆 Foley Scene Round Complete
      </div>
      <div style={{ color: 'var(--sd-muted, #79838D)' }}>Look at the TV screen for final scores!</div>
    </div>
  );
}

export default FoleyPlayer;
